import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import type { Member, MemberHousehold } from '@court-sync/shared';
import {
  memberDrawerFormSchema,
  memberFormToCreatePayload,
  memberFormToUpdatePayload,
  type MemberDrawerFormValues,
} from '@court-sync/shared';
import {
  Button,
  Drawer,
  Field,
  FieldError,
  FieldLabel,
  Input,
  PhoneField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@/components/ui';
import type { DefaultPhoneCountrySource } from '@/hooks/useDefaultPhoneCountry';
import { clubMembersService } from '@/services/clubMembers';
import { playerRatingService } from '@/services/playerRating';
import {
  MemberPersonSportRatingsPanel,
  type PendingSportRating,
} from '@/components/playerRating/PersonSportRatingsPanel';

const MEMBER_FORM_ID = 'club-member-form';

/** Select value reserved for "no household" since Radix Select rejects an empty string value. */
const NO_HOUSEHOLD_VALUE = '__none__';

/** Select value reserved for unset gender (edit mode only). */
const NO_GENDER_VALUE = '__unset__';

const createMemberDrawerFormSchema = memberDrawerFormSchema.superRefine((values, ctx) => {
  if (!values.gender) {
    ctx.addIssue({ code: 'custom', path: ['gender'], message: 'Gender is required' });
  }
});

interface MemberDrawerProps {
  clubId: string;
  member: Member | null;
  households: MemberHousehold[];
  defaultPhoneCountrySource?: DefaultPhoneCountrySource;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export function toFormValues(member: Member | null): MemberDrawerFormValues {
  if (!member) {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: '',
      householdId: '',
      isActive: true,
    };
  }

  return {
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email ?? '',
    phone: member.phone ?? '',
    dateOfBirth: member.dateOfBirth ? new Date(member.dateOfBirth).toISOString().slice(0, 10) : '',
    gender: member.gender ?? '',
    householdId: member.householdId ?? '',
    isActive: member.isActive,
  };
}

export function householdDisplayName(household: MemberHousehold, index: number): string {
  return household.name?.trim() ? household.name : `Household ${index + 1}`;
}

interface MemberDrawerFormProps {
  clubId: string;
  member: Member | null;
  households: MemberHousehold[];
  defaultPhoneCountrySource?: DefaultPhoneCountrySource;
  onClose: () => void;
  onSaved: () => void;
  onSubmittingChange: Dispatch<SetStateAction<boolean>>;
}

function MemberDrawerForm({
  clubId,
  member,
  households,
  defaultPhoneCountrySource,
  onClose,
  onSaved,
  onSubmittingChange,
}: MemberDrawerFormProps) {
  const isEditing = member != null;
  const [pendingRatings, setPendingRatings] = useState<PendingSportRating[]>([]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<MemberDrawerFormValues>({
    resolver: zodResolver(isEditing ? memberDrawerFormSchema : createMemberDrawerFormSchema),
    defaultValues: toFormValues(member),
  });

  useEffect(() => {
    onSubmittingChange(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const onSubmit = async (values: MemberDrawerFormValues) => {
    if (isEditing) {
      const res = await clubMembersService.updateMember(clubId, member.id, memberFormToUpdatePayload(values));
      if (res.success) {
        toast.success('Member updated');
        onSaved();
        onClose();
      } else {
        toast.error(res.error || 'Failed to update member');
      }
      return;
    }

    const res = await clubMembersService.createMember(clubId, memberFormToCreatePayload(values));
    if (!res.success || !res.data?.id) {
      toast.error(res.error || 'Failed to add member');
      return;
    }

    if (pendingRatings.length > 0) {
      const ratingsRes = await playerRatingService.updateMemberSportRatings(clubId, res.data.id, {
        ratings: pendingRatings,
      });
      if (!ratingsRes.success) {
        toast.error(ratingsRes.error ?? 'Member added, but ratings could not be saved');
        onSaved();
        onClose();
        return;
      }
    }

    toast.success('Member added');
    onSaved();
    onClose();
  };

  return (
    <form id={MEMBER_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field data-invalid={!!errors.firstName?.message}>
            <FieldLabel htmlFor="member-first-name">First name</FieldLabel>
            <Input
              id="member-first-name"
              aria-invalid={!!errors.firstName?.message}
              {...register('firstName')}
            />
            <FieldError>{errors.firstName?.message}</FieldError>
          </Field>

          <Field data-invalid={!!errors.lastName?.message}>
            <FieldLabel htmlFor="member-last-name">Last name</FieldLabel>
            <Input
              id="member-last-name"
              aria-invalid={!!errors.lastName?.message}
              {...register('lastName')}
            />
            <FieldError>{errors.lastName?.message}</FieldError>
          </Field>
        </div>

        <Field data-invalid={!!errors.email?.message}>
          <FieldLabel htmlFor="member-email">Email (optional)</FieldLabel>
          <Input
            id="member-email"
            type="email"
            aria-invalid={!!errors.email?.message}
            {...register('email')}
          />
          <FieldError>{errors.email?.message}</FieldError>
        </Field>

        <PhoneField
          control={control}
          name="phone"
          id="member-phone"
          label="Phone (optional)"
          defaultCountrySource={defaultPhoneCountrySource}
        />

        <Field>
          <FieldLabel htmlFor="member-dob">Date of birth (optional)</FieldLabel>
          <Input id="member-dob" type="date" {...register('dateOfBirth')} />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Used to tell adult and junior seats apart on shared memberships.
          </p>
        </Field>

        <Field data-invalid={!!errors.gender?.message}>
          <FieldLabel htmlFor="member-gender">Gender</FieldLabel>
          <Controller
            name="gender"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value?.trim() ? field.value : NO_GENDER_VALUE}
                onValueChange={(value) => field.onChange(value === NO_GENDER_VALUE ? '' : value)}
              >
                <SelectTrigger id="member-gender" aria-invalid={!!errors.gender?.message}>
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_GENDER_VALUE}>
                    {isEditing ? 'Not set' : 'Select gender'}
                  </SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          <FieldError>{errors.gender?.message}</FieldError>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Used for mens, ladies, and mixed matchups.
          </p>
        </Field>

        <MemberPersonSportRatingsPanel
          clubId={clubId}
          memberId={member?.id ?? null}
          draftMode={!isEditing}
          onPendingRatingsChange={!isEditing ? setPendingRatings : undefined}
        />

        <Field>
          <FieldLabel htmlFor="member-household">Household (optional)</FieldLabel>
          <Controller
            name="householdId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value?.trim() ? field.value : NO_HOUSEHOLD_VALUE}
                onValueChange={(value) => field.onChange(value === NO_HOUSEHOLD_VALUE ? '' : value)}
              >
                <SelectTrigger id="member-household">
                  <SelectValue placeholder="No household" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_HOUSEHOLD_VALUE}>No household</SelectItem>
                  {households.map((household, index) => (
                    <SelectItem key={household.id} value={household.id}>
                      {householdDisplayName(household, index)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>

        <div className="flex items-start gap-3">
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <Switch id="member-active" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
          <label htmlFor="member-active" className="cursor-pointer text-sm">
            <span className="font-medium text-gray-900 dark:text-white">On roster</span>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Turn off to archive. Archived members cannot be selected for new memberships.
            </p>
          </label>
        </div>
    </form>
  );
}

export function MemberDrawer({
  clubId,
  member,
  households,
  defaultPhoneCountrySource,
  isOpen,
  onClose,
  onSaved,
}: MemberDrawerProps) {
  const isEditing = member != null;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    setIsSubmitting(false);
    onClose();
  }, [onClose]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit member' : 'Add member'}
      description="Members can be grouped into a household so shared memberships can pick their seats from one place."
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" form={MEMBER_FORM_ID} isLoading={isSubmitting}>
            {isEditing ? 'Save member' : 'Add member'}
          </Button>
        </div>
      }
    >
      {isOpen ? (
        <MemberDrawerForm
          key={member?.id ?? 'new'}
          clubId={clubId}
          member={member}
          households={households}
          defaultPhoneCountrySource={defaultPhoneCountrySource}
          onClose={handleClose}
          onSaved={onSaved}
          onSubmittingChange={setIsSubmitting}
        />
      ) : null}
    </Drawer>
  );
}
